/**
 * SY-02 privacy: the age check (birth year) before any tracking, COPPA mode for under-13s,
 * consent (OVL-08) only where the region resolver says a consent tool is needed (EU, UK,
 * California), the parental gate and account deletion. Region and consent rules come from
 * @mp/adapters; this holds the game's state and the API calls.
 */
import {
  capabilities,
  consentModel,
  deviceTimeZone,
  isChild,
  requiresConsentBeforeTracking,
  resolveRegion,
  showsConsentTool,
  toConsentRequest,
  type Capabilities,
  type ConsentChoice,
  type ConsentStore,
  type Region,
} from '@mp/adapters/consent';
import { ROUTES, errorCode, isOffline, type ApiClient } from './api.js';
import type { Snapshot } from './dto.js';
import type { PlayerStore } from './store.js';
import type { SyncService } from './sync.js';

export type { ConsentChoice, Region } from '@mp/adapters/consent';

export type ParentAsk = { kind: 'sent'; expiresAt: number } | { kind: 'verified' } | { kind: 'invalid' } | { kind: 'offline' } | { kind: 'error'; code: string };
export type ParentLinkResult = 'ok' | 'expired' | 'unknown' | 'offline' | 'error';

export interface PrivacyDeps {
  store: PlayerStore;
  consent: ConsentStore;
  api: ApiClient;
  sync: SyncService;
  now: () => number;
  /** CDN country (or ?country= in dev/e2e); the time zone decides otherwise */
  country?: string | null;
  subdivision?: string | null;
  timeZone?: string | null;
}

export class Privacy {
  readonly region: Region;

  constructor(private d: PrivacyDeps) {
    this.region = resolveRegion({ country: d.country ?? null, subdivision: d.subdivision ?? null, timeZone: d.timeZone === undefined ? deviceTimeZone() : d.timeZone });
  }

  /** The consent tool exists here at all (settings shows it; OVL-08 asks once). */
  get consentTool(): boolean {
    return showsConsentTool(this.region);
  }

  /** EU/UK: nothing may be tracked, and the law wants the prompt up front (Flow A exception). */
  get askAtBoot(): boolean {
    return requiresConsentBeforeTracking(this.region) && (this.needsAge || this.needsConsent);
  }

  get needsAge(): boolean {
    const s = this.d.store.state;
    return s.birthYear === null && !s.coppa;
  }

  get needsConsent(): boolean {
    return this.d.consent.needsPrompt(this.region);
  }

  get coppa(): boolean {
    return this.d.store.state.coppa;
  }

  consentChoice(): ConsentChoice {
    return this.d.consent.effective(this.region);
  }

  /** Analytics may leave the device: age known, and consent where the model is opt-in. */
  trackingAllowed(): boolean {
    if (this.needsAge) return false;
    return this.consentChoice().analytics;
  }

  caps(): Capabilities {
    const s = this.d.store.state;
    return capabilities({ coppa: s.coppa, parentVerified: s.parentVerified, personalizedConsent: this.consentChoice().personalizedAds });
  }

  get model(): 'opt-in' | 'opt-out' | 'none' {
    return consentModel(this.region);
  }

  /** OVL-07 answer. Under-13 → COPPA mode at once, locally; the server confirms. */
  async setBirthYear(year: number): Promise<void> {
    const nowYear = new Date(this.d.now()).getFullYear();
    this.d.store.update((s) => {
      s.birthYear = year;
      if (isChild(year, nowYear)) s.coppa = true;
    });
    await this.pushAge();
  }

  /** Send the birth year when it has not reached the server yet (retried after sync). */
  async pushAge(): Promise<void> {
    const s = this.d.store.state;
    if (s.birthYear === null || s.prompts.ageSent || !this.d.api.enabled) return;
    try {
      const snap = await this.d.api.call<Snapshot>('POST', ROUTES.age, { birthYear: s.birthYear });
      this.d.store.update((x) => (x.prompts.ageSent = true));
      this.d.sync.adopt(snap);
    } catch (e) {
      if (!isOffline(e)) this.d.store.update((x) => (x.prompts.ageSent = true)); // refused for good: do not loop
    }
  }

  /** OVL-08 / settings: store the versioned choice locally and on the server. */
  async setConsent(choice: ConsentChoice): Promise<void> {
    const rec = this.d.consent.set(choice, this.region, this.d.now());
    this.d.store.update((s) => (s.prompts.consentAsked = rec.version));
    if (!this.d.api.enabled) return;
    try {
      const snap = await this.d.api.call<Snapshot>('POST', ROUTES.consent, toConsentRequest(rec));
      this.d.sync.adopt(snap);
    } catch {
      /* the local record rules on this device; the next change re-sends */
    }
  }

  /**
   * COPPA "email plus": the child names a parent's email; the API mails the parent a consent
   * link (POST /v1/parental/start). Only the parent's link verifies (confirmParent).
   */
  async askParent(parentEmail: string): Promise<ParentAsk> {
    try {
      const r = await this.d.api.call<{ sent: true; expiresAt: number }>('POST', ROUTES.parentalStart, { parentEmail: parentEmail.trim() });
      return { kind: 'sent', expiresAt: r.expiresAt };
    } catch (e) {
      if (isOffline(e)) return { kind: 'offline' };
      const code = errorCode(e);
      // the parent already agreed (maybe on another device): purchases are open
      if (code === 'already_verified' || code === 'not_needed') {
        this.d.store.update((s) => (s.parentVerified = true));
        return { kind: 'verified' };
      }
      if (code === 'bad_request' || code === 'invalid_request' || code === 'validation_failed') return { kind: 'invalid' };
      return { kind: 'error', code: code ?? 'error' };
    }
  }

  /** The parent's link (/parent/consent?token=, any device, no sign-in): record the consent. */
  async confirmParent(token: string): Promise<ParentLinkResult> {
    return this.parentLink(ROUTES.parentalConfirm, token);
  }

  /** The parent's withdraw link (/parent/revoke?token=). */
  async revokeParent(token: string): Promise<ParentLinkResult> {
    return this.parentLink(ROUTES.parentalRevoke, token);
  }

  private async parentLink(path: string, token: string): Promise<ParentLinkResult> {
    try {
      await this.d.api.request('POST', path, { body: { token }, auth: false, retries: 2 });
      return 'ok';
    } catch (e) {
      if (isOffline(e)) return 'offline';
      const code = errorCode(e);
      return code === 'token_expired' ? 'expired' : code === 'unknown_token' || code === 'not_found' ? 'unknown' : 'error';
    }
  }

  /** SY-02.3: schedule deletion (data removed within 30 days), then forget the device's copy. */
  async deleteAccount(): Promise<{ scheduledFor: number }> {
    const r = await this.d.api.call<{ scheduledFor: number }>('DELETE', ROUTES.deleteMe);
    return r;
  }
}

/** Parental gate question: a sum a young child is unlikely to solve (dev stand-in for the store flow). */
export function parentalQuestion(rand: () => number): { a: number; b: number; answer: number } {
  const a = 12 + Math.floor(rand() * 30);
  const b = 13 + Math.floor(rand() * 40);
  return { a, b, answer: a + b };
}

/** Years offered by the age picker, newest first. */
export function birthYears(nowYear: number, span = 100): number[] {
  return Array.from({ length: span }, (_, i) => nowYear - 3 - i);
}
